import {
  CustomDateRangePicker,
  DataTable,
  formatDataToLabelValue,
  getDateOfTheBeginningOfCurrentMonth,
  getDateOfToday,
  IOptions,
  MainButton,
  ModalDelete,
  Select,
  STATUSPAGE,
} from '@repo/ui';
import {
  GetAllCoursesByPersonUseCaseInterface,
  GetAllEntitiesByPersonUseCaseInterface,
  GetAllPersonProfileUseCaseInterface,
} from 'domain/usecases';
import {
  ContentState,
  ContentStateStatus,
} from 'presentation/app/components/content-state';
import { EntityAndCourseFiltersByProfiles } from 'presentation/app/components/entity-and-course-filters-by-profiles';
import {
  useEntityAndCourseFiltersByProfiles,
  useEntityAndCourseFiltersTouched,
} from 'presentation/app/hooks/entity-and-course-filters';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Col, Form, Label, Row } from 'reactstrap';
import { Card } from 'reactstrap';

import noDataBg from '../../../../../assets/images/no-task.png';
import { makeColumnsPersonCompletedEvaluations } from '../../../common/columns/columns-person-completed-evaluations';
import { ModalCompletedEvaluationDetails } from '../../../components/modal-completed-evaluation-details';
import { usePersonCompletedEvaluationsTab } from '../hooks';

interface IPersonCompletedEvaluationsTabProps {
  profiles: GetAllPersonProfileUseCaseInterface.Result;
  courses: GetAllCoursesByPersonUseCaseInterface.Result;
  organizations: GetAllEntitiesByPersonUseCaseInterface.Result;
}

export const PersonCompletedEvaluationsTab = ({
  profiles,
  courses,
  organizations,
}: IPersonCompletedEvaluationsTabProps) => {
  const [isOpenModalDetails, setIsOpenModalDetails] = useState(false);
  const [selectedGroups, setSelectedGroups] = useState<IOptions[]>([]);
  const [period, setPeriod] = useState<Date[]>([
    getDateOfTheBeginningOfCurrentMonth(),
    getDateOfToday(),
  ]);
  const [selectedEvaluationId, setSelectedEvaluationId] = useState<string>('');
  const [isOpenModalDelete, setIsOpenModalDelete] = useState(false);

  const { id } = useParams();

  const { touched, validate } = useEntityAndCourseFiltersTouched();
  const {
    orgOptions,
    courseOptions,
    selectedCourse,
    onCourseChange,
    hasValidProfile,
    selectedOrganization,
    onOrganizationChange,
  } = useEntityAndCourseFiltersByProfiles({
    courses,
    organizations,
    profiles,
  });
  const {
    groups,
    requestStatus,
    loadingGroups,
    loadingDetails,
    completedEvaluations,
    requestGetAllGroupsUser,
    completedEvaluationDetails,
    loadingCompletedEvaluations,
    loadingDeleteEvaluation,
    requestGetCompletedEvaluationDetails,
    requestGetAllPersonCompletedEvaluations,
    requestDeleteCompletedEvaluation,
  } = usePersonCompletedEvaluationsTab();
  const contentStateStatus: ContentStateStatus | null =
    requestStatus === STATUSPAGE.DONE
      ? completedEvaluations.length > 0
        ? null
        : STATUSPAGE.DONE
      : requestStatus;

  const toggleModalDelete = () => setIsOpenModalDelete(!isOpenModalDelete);

  const toggleModalDetails = (evaluationId?: string) => {
    if (evaluationId) {
      setSelectedEvaluationId(evaluationId);
      requestGetCompletedEvaluationDetails({
        surveyId: evaluationId,
      });
    }

    setIsOpenModalDetails(!isOpenModalDetails);
  };

  const handleFiltersSubmit = () => {
    if (!validate(selectedOrganization, selectedCourse)) return;

    const filtersToSubmit = {
      id,
      courseHashId: selectedCourse?.value || '',
      organizationHashId: selectedOrganization?.value || '',
      ['groupHashIds[]']: selectedGroups.map((group) => group.value),
      startsAt: period[0].toISOString().split('T')[0],
      endsAt: period[1].toISOString().split('T')[0],
    };

    requestGetAllPersonCompletedEvaluations(filtersToSubmit);
  };

  useEffect(() => {
    requestGetAllGroupsUser({
      courseId: selectedCourse?.value || '',
      entityId: selectedOrganization?.value || '',
    });
  }, [selectedCourse, selectedOrganization]);

  return (
    <Col md={12} className="mt-5">
      <Form
        autoComplete="off"
        onSubmit={(e) => {
          e.preventDefault();
          handleFiltersSubmit();
        }}
      >
        <Card className="p-3">
          <Row className="g-3">
            <EntityAndCourseFiltersByProfiles
              hasValidProfile={hasValidProfile}
              orgColSize={3}
              courseColSize={3}
              courses={courseOptions}
              organizations={orgOptions}
              selectedCourse={selectedCourse}
              selectedOrganization={selectedOrganization}
              onOrganizationChange={(organization) => {
                onOrganizationChange(organization);
                setSelectedGroups([]);
              }}
              onCourseChange={(course) => {
                onCourseChange(course);
                setSelectedGroups([]);
              }}
              touched={touched}
            />

            <Col md={3}>
              <Label className="form-label">Grupos</Label>
              <Select
                isMulti
                isClearable
                id="groups"
                name="groups"
                onChange={(selected) => {
                  setSelectedGroups((selected ?? []) as IOptions[]);
                }}
                placeholder="Todos os grupos"
                options={formatDataToLabelValue(groups as never, 'id', 'nome')}
                isLoading={loadingGroups}
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                value={selectedGroups as any}
              />
            </Col>

            <Col md={3}>
              <Row>
                <Label htmlFor="period" className="form-label">
                  Período
                </Label>
                <CustomDateRangePicker
                  initialStartDate={new Date(period[0])}
                  initialEndDate={new Date(period[1])}
                  cleanable={false}
                  placement="auto"
                  value={
                    period.length > 0
                      ? [new Date(period[0]), new Date(period[1])]
                      : null
                  }
                  menuClassName="custom-datepicker-menu"
                  onChange={(e: Date[]) => {
                    setPeriod(e);
                  }}
                />
              </Row>
            </Col>

            <Row>
              <Col
                md={12}
                className="d-flex align-items-center justify-content-end mt-4 mx-4 gap-3"
              >
                <MainButton
                  type="submit"
                  isLoading={loadingCompletedEvaluations}
                >
                  Filtrar
                </MainButton>
              </Col>
            </Row>
          </Row>
        </Card>
      </Form>

      {contentStateStatus !== null && (
        <ContentState
          status={contentStateStatus}
          className="mt-3"
          action={
            contentStateStatus === STATUSPAGE.ERROR
              ? {
                  label: 'Tentar novamente',
                  icon: 'ri-refresh-line',
                  onClick: handleFiltersSubmit,
                }
              : undefined
          }
        />
      )}

      {requestStatus === STATUSPAGE.DONE && completedEvaluations.length > 0 && (
        <Card className="p-3 pt-0">
          <DataTable
            actions={[
              {
                id: 1,
                name: 'view',
                label: 'Visualizar detalhes da avaliação',
                icon: 'ri-file-search-line',
                classNameColorIcon: 'none',
                onPress: (cellProps) => {
                  toggleModalDetails(cellProps.id);
                },
              },
            ]}
            canSearch
            data={completedEvaluations || []}
            columns={makeColumnsPersonCompletedEvaluations()}
            loading={loadingCompletedEvaluations}
            defaultSortAsc
            defaultSortFieldId={1}
            noDataBg={noDataBg}
          />
        </Card>
      )}

      <ModalCompletedEvaluationDetails
        loading={loadingDetails}
        isOpen={isOpenModalDetails}
        toggle={toggleModalDetails}
        details={completedEvaluationDetails}
        toggleModalDelete={toggleModalDelete}
        clearSelectedEvaluationId={() => setSelectedEvaluationId('')}
      />

      <ModalDelete
        isOpen={isOpenModalDelete}
        toggle={toggleModalDelete}
        title="Excluir Avaliação?"
        subtitle="Tem certeza que deseja excluir esta avaliação?"
        loading={loadingDeleteEvaluation}
        handleDelete={() =>
          requestDeleteCompletedEvaluation(
            {
              surveyId: selectedEvaluationId,
            },
            () => {
              toggleModalDelete();
              toggleModalDetails();
              setSelectedEvaluationId('');
              requestGetAllPersonCompletedEvaluations({
                id,
                courseHashId: selectedCourse?.value || '',
                organizationHashId: selectedOrganization?.value || '',
                ...(selectedGroups.length > 0 && {
                  ['groupHashIds[]']: selectedGroups.map(
                    (group) => group.value,
                  ),
                }),
                startsAt: period[0].toISOString(),
                endsAt: period[1].toISOString(),
              });
            },
          )
        }
      />
    </Col>
  );
};
